import { useEffect, useMemo, useState } from 'react';

import { bindAll } from 'bind-event-listener';

import { type ConversationChannel } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { AssistanceServiceImpl } from '@atlassian/conversation-assistant-service/services/assistance-service/main';

import { useAISmartButtonContext } from '../../context';
import { isBrowser } from '../../utils/is-browser';
import { schedulePostPaint } from '../../utils/schedule-post-paint';

const LOOK_BACK_PERIOD_MS = 36 * 60 * 60 * 1000;
const CACHE_TTL_MS = 5 * 60 * 1000;
const CACHE_KEY = 'rovo-button-unread-status-v1';

type UnreadConversationSummary =
	| {
			hasUnread: true;
			mostRecentUnread: {
				id: string;
				name: string | null;
			};
			unreadCount: number;
	  }
	| {
			hasUnread: false;
			mostRecentUnread: null;
			unreadCount: 0;
	  };

type CachedResult = {
	cachedAt: number;
	hasUnread: boolean;
	mostRecentUnread: { id: string; name: string | null } | null;
	unreadCount: number;
};

function readCache(): UnreadConversationSummary | null {
	if (!isBrowser()) {
		return null;
	}
	try {
		const raw = sessionStorage.getItem(CACHE_KEY);
		if (!raw) {
			return null;
		}
		const parsed: CachedResult = JSON.parse(raw);
		if (Date.now() - parsed.cachedAt < CACHE_TTL_MS) {
			const { cachedAt: _cachedAt, ...result } = parsed;
			// Migrate cache entries written before unreadCount was added
			if (result.unreadCount === undefined) {
				(result as Record<string, unknown>).unreadCount = result.hasUnread ? 1 : 0;
			}
			return result as UnreadConversationSummary;
		}
		sessionStorage.removeItem(CACHE_KEY);
		return null;
	} catch {
		return null;
	}
}

function writeCache(result: UnreadConversationSummary): void {
	if (!isBrowser()) {
		return;
	}
	try {
		const toStore: CachedResult = { ...result, cachedAt: Date.now() };
		sessionStorage.setItem(CACHE_KEY, JSON.stringify(toStore));
	} catch {
		// sessionStorage may be unavailable (private browsing quota, etc.) — fail silently
	}
}

const NO_UNREAD: UnreadConversationSummary = {
	hasUnread: false,
	unreadCount: 0,
	mostRecentUnread: null,
};

/**
 * Fetches conversation channel data after the page has painted (post-vc90) and
 * derives whether the user has any unread conversations from the past 36 hours.
 *
 * Results are cached in sessionStorage with a 5-minute TTL to avoid redundant
 * fetches on SPA navigations. The cache is refreshed on window focus once expired.
 *
 * On any fetch failure, silently returns `{ hasUnread: false, mostRecentUnread: null }`
 * so the button always renders without an indicator rather than in an error state.
 */
export function useUnreadConversationStatus({ enabled }: { enabled: boolean }): UnreadConversationSummary {
	const [status, setStatus] = useState<UnreadConversationSummary>(() =>
		enabled ? (readCache() ?? NO_UNREAD) : NO_UNREAD,
	);
	const { conversationAssistantConfig, product } = useAISmartButtonContext();
	const baseUrl = conversationAssistantConfig?.assistanceServiceConfig?.baseUrl;
	const headers = conversationAssistantConfig?.assistanceServiceConfig?.headers;
	const assistanceService = useMemo(
		() =>
			new AssistanceServiceImpl({
				product,
				experienceId: 'ai-mate',
				baseUrl,
				headers,
			}),
		[product, baseUrl, headers],
	);

	useEffect(() => {
		if (!enabled) {
			setStatus(NO_UNREAD);
			return;
		}

		let cancelled = false;

		const fetchAndUpdate = async () => {
			const cached = readCache();
			if (cached) {
				if (!cancelled) {
					setStatus((prev) => {
						if (prev.hasUnread === cached.hasUnread && prev.unreadCount === cached.unreadCount) {
							return prev;
						}
						return cached;
					});
				}
				return;
			}

			try {
				const channels: ConversationChannel[] = await assistanceService.getConversationChannels();
				if (cancelled) {
					return;
				}

				const now = Date.now();
				const recentUnread = channels
					.filter((channel) => {
						const isUnread = channel.readStatus === false || channel.readStatus === null;
						const timestamp = channel.last_message_timestamp;
						const isRecent = !!timestamp && now - new Date(timestamp).getTime() < LOOK_BACK_PERIOD_MS;
						return isUnread && isRecent;
					})
					.sort((a, b) => {
						const aTime = a.last_message_timestamp
							? new Date(a.last_message_timestamp).getTime()
							: 0;
						const bTime = b.last_message_timestamp
							? new Date(b.last_message_timestamp).getTime()
							: 0;
						return bTime - aTime;
					});

				const result: UnreadConversationSummary =
					recentUnread.length > 0
						? {
								hasUnread: true,
								unreadCount: recentUnread.length,
								mostRecentUnread: {
									id: String(recentUnread[0].id),
									name: recentUnread[0].name ?? null,
								},
							}
						: NO_UNREAD;

				writeCache(result);
				setStatus(result);
			} catch {
				// Fail silently — no indicator is the safe default
				if (!cancelled) {
					setStatus(NO_UNREAD);
				}
			}
		};

		// Defer the initial fetch until after paint/idle (post-vc90 safe)
		const cancelPostPaint = schedulePostPaint(() => {
			void fetchAndUpdate();
		});

		// Refresh on window focus once the cache has expired
		const unbindFocus = isBrowser()
			? bindAll(window, [
					{
						type: 'focus',
						listener: () => {
							void fetchAndUpdate();
						},
					},
				])
			: null;

		return () => {
			cancelled = true;
			cancelPostPaint();
			unbindFocus?.();
		};
	}, [assistanceService, enabled]);

	return status;
}
